// https://ui.shadcn.com/docs/components/calendar#date-picker
// At the time of this writing, the shadcn docs do not align with the latest version of date-picker
// This github issue documents the upgrade path https://github.com/shadcn-ui/ui/issues/1574#issuecomment-2314717389

import { buttonVariants, cn } from '@seen/ui'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import type * as React from 'react'
import type { JSX } from 'react'
import { DayFlag, DayPicker, SelectionState, UI } from 'react-day-picker'

export type MonthCalendarProps = React.ComponentProps<typeof DayPicker>

function MonthCalendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  ...props
}: MonthCalendarProps): JSX.Element {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      className={cn('p-3', className)}
      classNames={{
        [UI.Months]: 'relative',
        [UI.Month]: 'ml-0 space-y-4',
        [UI.MonthCaption]: 'flex h-7 items-center justify-center',
        [UI.CaptionLabel]: 'text-sm font-medium',
        [UI.PreviousMonthButton]: cn(
          buttonVariants({ variant: 'ghost' }),
          'absolute top-0 left-1 size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        [UI.NextMonthButton]: cn(
          buttonVariants({ variant: 'ghost' }),
          'absolute top-0 right-1 size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        [UI.MonthGrid]: 'w-full border-collapse space-y-1',
        [UI.Weekdays]: 'flex',
        [UI.Weekday]:
          'w-7 rounded-md text-[0.8rem] font-normal text-gray-400 text-muted-foreground',
        [UI.Week]: 'mt-1 flex w-full',
        [UI.Day]:
          'relative h-7 w-7 rounded-md p-0 text-center text-sm focus-within:relative focus-within:z-20 has-aria-[selected]:bg-accent first:has-aria-[selected]:rounded-l-md last:has-aria-[selected]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md hover:[&>button]:bg-gray-100',
        [UI.DayButton]: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-7 p-0 font-normal hover:bg-gray-500 hover:text-gray-500 aria-selected:opacity-100'
        ),
        [SelectionState.range_end]: 'day-range-end',
        [SelectionState.selected]:
          'bg-gray-900 hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground [&>button]:text-gray-50 [&>button]:hover:bg-gray-500 [&>button]:hover:text-gray-50',
        [SelectionState.range_middle]:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        [DayFlag.today]: 'bg-accent text-accent-foreground',
        [DayFlag.outside]:
          'day-outside text-muted-foreground opacity-50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        [DayFlag.disabled]: 'text-muted-foreground opacity-50',
        [DayFlag.hidden]: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ ...props }) =>
          props.orientation === 'right' ? (
            <IconChevronRight
              {...props}
              className={cn('size-4', props.className)}
            />
          ) : (
            <IconChevronLeft
              {...props}
              className={cn('size-4', props.className)}
            />
          ),
      }}
      {...props}
    />
  )
}
MonthCalendar.displayName = 'MonthCalendar'

export { MonthCalendar }
